import React from 'react';
import { CompanyListItem, ICompany } from 'types/company';
import { TinyCommunity } from 'types/company/community';
import { UserFeedData } from '../useUserFeedSWR';
import ShowRoomUpdate from 'pages/Rooms/Updates/Show/Update';
import CommunityUpdate from 'pages/Community/Overview/CommunityUpdates/Update';
import BoardroomUpdate from 'pages/Boardroom/Updates/Update';
import { communityUrls, companyUrls } from 'urls';
import Update from 'pages/Company/Overview/sections/updates/Show/Update';
import { asBoardRoomAuthor, asCompanyAuthor, asRoomAuthor } from 'domain/shared/author';
import { getPostedBy } from 'pages/Community/Overview/CommunityUpdates/UpdateList';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import { bluePlanetTheme } from 'ui/theme';
import { halfSpacing } from 'ui/theme/themeConstants';
import Chip from 'ui/elements/Chip';
import Tooltip from 'ui/elements/Tooltip';
import { CompanyUpdate } from 'types/content';
import PostImpression from 'domain/Updates/shared/PostImpression';

const SuggestionChipContainer = styled.div`
  margin-top: ${halfSpacing};
  ${bluePlanetTheme.breakpoints.down('sm')} {
    margin-left: ${halfSpacing};
  }
`;

const highlightFlash = keyframes`
  0% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const UpdateWrapper = styled.div<{ isNewlyPosted?: boolean }>`
  ${props =>
    props.isNewlyPosted &&
    css`
      animation: ${highlightFlash} 0.3s ease-out;
    `}
`;

function formatSuggestionReason(suggestionReason: string): string | null {
  try {
    const parsed = JSON.parse(suggestionReason);

    if (parsed.type === 'industry-match' && parsed.industry) {
      return `Suggested because you're interested in ${parsed.industry}`;
    }

    return null;
  } catch {
    // If JSON parsing fails, return null to indicate no suggestion should be shown
    return null;
  }
}

const ShowUpdate = React.forwardRef<
  HTMLDivElement,
  {
    update: UserFeedData;
    communities: TinyCommunity[];
    companies: CompanyListItem[];
    companyProfiles: ICompany[];
    isNewlyPosted?: boolean;
  }
>(({ communities, companies, update, companyProfiles, isNewlyPosted }, ref) => {
  if (update.type === 'room') {
    const room = update.roomUpdate.room;
    const company = companies.find(c => c.id === room.companyId);
    const viewRoomHref = company && room && companyUrls.rooms.view(company.slug, room.id);
    const viewCompanyHref = company && companyUrls.overview(company.slug, 'profile');
    return (
      <UpdateWrapper ref={ref} isNewlyPosted={isNewlyPosted}>
        <PostImpression contentId={update.roomUpdate.update.id} surface="Feed">
          <ShowRoomUpdate
            key={`update-${update.roomUpdate.update.id}`}
            update={update.roomUpdate}
            creator={update.roomUpdate.update.createdBy}
            onChange={() => {}}
            canEditPost={false}
            author={asRoomAuthor(
              update.roomUpdate.update.createdBy,
              company,
              viewCompanyHref,
              room?.name,
              viewRoomHref,
            )}
          />
        </PostImpression>
      </UpdateWrapper>
    );
  } else if (update.type === 'boardroom') {
    const maybeCompany = companies.find(c => c.id === update.boardroomUpdate.company.id);
    const maybeCommunity = communities.find(c => c.id === update.boardroomUpdate.company.id);
    const companyHref = maybeCommunity
      ? communityUrls.overview(maybeCommunity.slug)
      : maybeCompany
        ? companyUrls.overview(maybeCompany.slug, 'updates')
        : undefined;
    const roomHref = maybeCommunity
      ? communityUrls.overview(maybeCommunity.slug)
      : maybeCompany
        ? companyUrls.boardroom.overview(maybeCompany.slug)
        : undefined;

    return (
      <UpdateWrapper ref={ref} isNewlyPosted={isNewlyPosted}>
        <PostImpression contentId={update.boardroomUpdate.update.id} surface="Feed">
          <BoardroomUpdate
            key={`update-${update.boardroomUpdate.update.id}`}
            boardroomUpdate={update.boardroomUpdate}
            creator={update.boardroomUpdate.update.createdBy}
            companyId={update.boardroomUpdate.company.id}
            canEditPost={false}
            onChange={() => {}}
            author={asBoardRoomAuthor(
              update.boardroomUpdate.update.createdBy,
              maybeCompany || maybeCommunity,
              companyHref,
              roomHref,
            )}
          />
        </PostImpression>
      </UpdateWrapper>
    );
  } else if (update.type === 'community') {
    const community = communities.find(c => c.id === update.communityUpdate.communityId);
    if (!community) return null;

    const postedBy = getPostedBy(update.communityUpdate, companyId =>
      communityUrls.companies.viewFromOverview(community.slug, companyId, 'community-feed'),
    );

    return (
      <UpdateWrapper ref={ref} isNewlyPosted={isNewlyPosted}>
        <PostImpression contentId={update.communityUpdate.update.id} surface="Feed">
          <CommunityUpdate
            key={`update-${update.communityUpdate.update.id}`}
            update={update.communityUpdate}
            creator={update.communityUpdate.update.createdBy}
            canEditPost={false}
            onEdit={() => {}}
            community={community}
            onArchive={() => {}}
            postedBy={postedBy}
            shownIn="dashboard"
          />
        </PostImpression>
      </UpdateWrapper>
    );
  } else if (update.type === 'company') {
    const company =
      companyProfiles.find(c => c.id === update.companyUpdate.companyId) ||
      companies.find(c => c.id === update.companyUpdate.companyId);

    return company ? (
      <UpdateWrapper ref={ref} isNewlyPosted={isNewlyPosted}>
        <CompanyFeedUpdate companyUpdate={update.companyUpdate} company={company} />
      </UpdateWrapper>
    ) : null;
  }
  return null;
});

ShowUpdate.displayName = 'ShowUpdate';

function CompanyFeedUpdate({
  companyUpdate,
  company,
}: {
  companyUpdate: CompanyUpdate;
  company: CompanyListItem | ICompany;
}) {
  const suggestionReasonText = companyUpdate.suggestionReason
    ? formatSuggestionReason(companyUpdate.suggestionReason)
    : null;

  return (
    <PostImpression contentId={companyUpdate.update.id} surface="Feed">
      {companyUpdate.suggestionReason && (
        <SuggestionChipContainer>
          <Tooltip title={suggestionReasonText ?? ''}>
            <span>
              <Chip color="grey-light" label="Matches your interests" />
            </span>
          </Tooltip>
        </SuggestionChipContainer>
      )}
      <Update
        key={`update-${companyUpdate.update.id}`}
        update={companyUpdate}
        creator={companyUpdate.update.createdBy}
        canEditPost={false}
        company={company}
        author={asCompanyAuthor(companyUpdate.update.createdBy, company, companyUrls.overview(company.slug, 'updates'))}
        onArchive={() => {}}
        onEdit={() => {}}
        isAtProfile={false}
      />
    </PostImpression>
  );
}

export default ShowUpdate;
